<template>
  <list-layout
      :title="$t('tariff_periods') | startcase"
      :search-filter="filters.search"
      :apply-filters="applyFilters"
      no-counts
      no-search
      no-apply-btn
  >
    <modal
        id="distributions-export-modal"
        :customTitle="$t('export_tariff_period_list') | capitalize"
        modal-class="custom-modal edit"
        modalType="edit"
        size="xl"
        centered
        hide-footer>
      <distributions-export-form :count="count" :selected-table="getSelectedTable()"/>
    </modal>
    <template v-slot:heading-right>
      <b-button
          class="ml-4"
          variant="secondary"
          :to="{'name': 'distribution-create'}"
          v-if="permissions.pages[Pages.DistributionList].canWrite"
      >{{ $t("create_new_tariff_period") | startcase }}
      </b-button>
    </template>
    <template v-slot:beforeFilters>
      <b-row>
        <b-col>
          <filter-builder
              :id="userPreferenceNames.DISTRIBUTION_LIST_FILTER"
              :filters="getFiltersWithOptions()"
              :apply-filters="applyFilters"
              :reset-filters="resetFilters"
              :filters-pending="filtersPending"
              :filters-applied="filtersApplied"
              @sorted="onSorted"
              @filter="onFilter"
          />
        </b-col>
      </b-row>
      <b-row class="text-center mb-3" align-v="center" align-h="center">
        <b-col cols="3">
          <b-form-radio-group
              v-model="filters.distributionMethodology.value"
              buttons
              button-variant="outline-secondary"
              name="radios-btn-outline"
              class="radio-buttons"
          >
            <b-form-radio :value="distributionMethodologies.STANDARD">
              {{ $t("standard") | capitalize }}
            </b-form-radio>
            <b-form-radio :value="distributionMethodologies.INTERNATIONAL">
              {{ $t("international") | capitalize }}
            </b-form-radio>
            <b-form-radio :value="distributionMethodologies.CONNECT">
              {{ $t("connect_reproduction") }}
            </b-form-radio>
          </b-form-radio-group>
        </b-col>

        <b-col cols="2">
          <b-form-radio-group
              v-model="displayTable"
              buttons
              button-variant="outline-secondary"
              name="radios-btn-outline"
              class="radio-buttons"
          >
            <b-form-radio :value="distributionsTableDisplayOptions.DEFAULT">
              {{ $t("default") | capitalize }}
            </b-form-radio>
            <b-form-radio :value="distributionsTableDisplayOptions.STATISTICS">
              {{ $t("statistics") | capitalize }}
            </b-form-radio>
          </b-form-radio-group>
        </b-col>
        <b-col v-if="displayTable === distributionsTableDisplayOptions.STATISTICS" cols="2">
          <b-form-radio-group
              v-model="statisticsValues"
              buttons
              button-variant="outline-secondary"
              name="radios-btn-outline"
              class="radio-buttons"
              :disabled="displayTable !== distributionsTableDisplayOptions.STATISTICS"
          >
            <b-form-radio :value="distributionsStatisticsValuesOptions.PLAYCOUNT">
              {{ $t("playcount") | capitalize }}
            </b-form-radio>
            <b-form-radio :value="distributionsStatisticsValuesOptions.RECORDINGS">
              {{ $t("recordings") | capitalize }}
            </b-form-radio>
          </b-form-radio-group>
        </b-col>
        <b-col v-if="displayTable === distributionsTableDisplayOptions.STATISTICS" cols="4">
          <b-form-radio-group
              v-model="statisticsColumns"
              buttons
              button-variant="outline-secondary"
              name="radios-btn-outline"
              class="radio-buttons"
              :disabled="displayTable !== distributionsTableDisplayOptions.STATISTICS"
          >
            <b-form-radio :value="distributionsStatisticsColumnsOptions.ALL">
              {{ $t("all") | capitalize }}
            </b-form-radio>
            <b-form-radio :value="distributionsStatisticsColumnsOptions.COUNT">
              {{ $t("count") | capitalize }}
            </b-form-radio>
            <b-form-radio :value="distributionsStatisticsColumnsOptions.PERCENTAGE">
              {{ $t("percentage") | capitalize }}
            </b-form-radio>
            <b-form-radio :value="distributionsStatisticsColumnsOptions.DELTA">
              {{ $t("delta") | capitalize }}
            </b-form-radio>
          </b-form-radio-group>
        </b-col>
      </b-row>
    </template>

    <template v-slot:table>
      <t-table
          :columns="columns"
          :options="options"
          :limit="limit"
          ref="distributions"
          id="distributions-detailed-table"
          :column-mode="columnMode"
          @loaded="onLoaded"
          @sorted="onSorted"
          @pagination="onPaginationClearCached"
          @loading="onLoading"
          :cached-data="cachedData"
      >
        <template v-slot:filters>
          <div class="calculation-times">
            <div>
              {{ $t("last_calculation") | capitalize }}:
              {{ latestCalculation ? parseDate(latestCalculation, $config.DATETIME_FORMAT) : "-" }}
            </div>
            <div>
              {{ $t("next_calculation") | capitalize }}: {{ parseDate(nextCalculation, $config.DATETIME_FORMAT) }}
            </div>
          </div>
        </template>

        <template v-slot:merge>
            <span>
              <b-button size="sm" variant="outline-primary" @click="exportData" class="ml-2">
                <fa-icon size="sm" :icon="['fa', 'cloud-upload-alt']"/>
                {{ $t("export") | capitalize }}
              </b-button>
            </span>
        </template>
        <template v-slot:counts>
          <div class="d-flex">
            <pagination
                class="smaller p-0"
                :page="page"
                :count="count"
                :loading="loading"
                v-model="limit"
                @limit="onLimitClearCached"
              />
            </div>
          </template>
          <template v-slot:id="{ row }">
            <database-link :type="linkTypes.DISTRIBUTION" :item-id="row.id">{{ row.id }}</database-link>
          </template>
          <template v-slot:tariff="{ row }">
            {{ tariffDisplay(row.tariff) }}
          </template>
          <template v-slot:proxyTariff="{ row }">
            {{ tariffDisplay(row.proxyTariff) }}
          </template>
          <!-- College -->
          <template v-slot:college="{ row }">
            {{ $t(row.college) | capitalize }}
          </template>
          <template v-slot:actions="{ row }">
            <div class="d-flex justify-content-end">
              <table-action
                  :title="$t('delete') | lowercase"
                  v-if="permissions.pages[Pages.DistributionList].canWrite"
                  @click="onDelete(row.id)"
                  :icon="['fas', 'trash']"
                  :disabled="row.computedConsumerFields && !row.computedConsumerFields.deletionAllowed"
              />

            <!-- Deletion Confirmation Modal -->
            <modal
                :id="'confirmation-modal-' + row.id"
                :customTitle="$t('delete_tariff_period') | capitalize"
                modal-class="custom-modal edit"
                modalType="edit"
                size="md"
                centered
                @ok="onConfirmDelete(row.id)"
                okTitle="Confirm">
              <div>{{ $t("do_you_really_want_to_delete_tariff_period") | capitalize }}?</div>
            </modal>
          </div>
        </template>

        <template v-slot:calculationTime="{ row }">
          <date-time-cell :date-time="getTransactionCalculationData(row)?.calculationTime"/>
        </template>

        <template v-slot:revenueTotal="{ row }">
          {{
            getCurrencyAmountFormatted(row.revenueCurrency, parseFloat(row.revenueMaker ?? 0) + parseFloat(row.revenuePerformer ?? 0))
          }}
        </template>

        <template v-slot:vppPerformer="{ row }">
          {{
            getCurrencyAmountFormatted(row.revenueCurrency, row.vpp?.performer)
          }}
        </template>

        <template v-slot:vppMaker="{ row }">
          {{
            getCurrencyAmountFormatted(row.revenueCurrency, row.vpp?.maker)
          }}
        </template>


        <template v-slot:vppDeltaPerformer="{ row }">
          {{
            getCurrencyDeltaFormatted(row.revenueCurrency, getTransactionCalculationStatisticsData(row)?.vppDelta?.performer)
          }}
        </template>

        <template v-slot:vppDeltaMaker="{ row }">
          {{
            getCurrencyDeltaFormatted(row.revenueCurrency, getTransactionCalculationStatisticsData(row)?.vppDelta?.maker)
          }}
        </template>


        <template v-slot:matchedPlaycountPercentage="{ row }">
          {{
            getPercentageAmountFormatted(getTransactionCalculationStatisticsData(row)?.matchedPlaycountPercentage)
          }}
        </template>

        <template v-slot:matchedPlaycountPercentageDelta="{ row }">
          {{
            getPercentageDeltaFormatted(getTransactionCalculationStatisticsData(row)?.matchedPlaycountPercentageDelta)
          }}
        </template>

        <template v-slot:payablePlaycount="{ row }">
          {{
            getTransactionCalculationStatisticsData(row)?.payablePlaycount
          }}
        </template>

        <template v-slot:payablePlaycountPercentage="{ row }">
          {{
            getPercentageAmountFormatted(getTransactionCalculationStatisticsData(row)?.payablePlaycountPercentage)
          }}
        </template>

        <template v-slot:payablePlaycountPercentageDelta="{ row }">
          {{
            getPercentageDeltaFormatted(getTransactionCalculationStatisticsData(row)?.payablePlaycountPercentageDelta)
          }}
        </template>

        <template v-slot:nonEligiblePlaycount="{ row }">
          {{
            getTransactionCalculationStatisticsData(row)?.nonEligiblePlaycount
          }}
        </template>

        <template v-slot:nonEligiblePlaycountPercentage="{ row }">
          {{
            getPercentageAmountFormatted(getTransactionCalculationStatisticsData(row)?.nonEligiblePlaycountPercentage)
          }}
        </template>

        <template v-slot:nonEligiblePlaycountPercentageDelta="{ row }">
          {{
            getPercentageDeltaFormatted(getTransactionCalculationStatisticsData(row)?.nonEligiblePlaycountPercentageDelta)
          }}
        </template>

        <template v-slot:ownershipConflictPlaycount="{ row }">
          {{
            getTransactionCalculationStatisticsData(row)?.ownershipConflictPlaycount
          }}
        </template>

        <template v-slot:ownershipConflictPlaycountPercentage="{ row }">
          {{
            getPercentageAmountFormatted(getTransactionCalculationStatisticsData(row)?.ownershipConflictPlaycountPercentage)
          }}
        </template>

        <template v-slot:ownershipConflictPlaycountPercentageDelta="{ row }">
          {{
            getPercentageDeltaFormatted(getTransactionCalculationStatisticsData(row)?.ownershipConflictPlaycountPercentageDelta)
          }}
        </template>

        <template v-slot:lineupConflictPlaycount="{ row }">
          {{
            getTransactionCalculationStatisticsData(row)?.lineupConflictPlaycount
          }}
        </template>

        <template v-slot:lineupConflictPlaycountPercentage="{ row }">
          {{
            getPercentageAmountFormatted(getTransactionCalculationStatisticsData(row)?.lineupConflictPlaycountPercentage)
          }}
        </template>

        <template v-slot:lineupConflictPlaycountPercentageDelta="{ row }">
          {{
            getPercentageDeltaFormatted(getTransactionCalculationStatisticsData(row)?.lineupConflictPlaycountPercentageDelta)
          }}
        </template>

        <template v-slot:mandateConflictPlaycount="{ row }">
          {{
            getTransactionCalculationStatisticsData(row)?.mandateConflictPlaycount
          }}
        </template>

        <template v-slot:mandateConflictPlaycountPercentage="{ row }">
          {{
            getPercentageAmountFormatted(getTransactionCalculationStatisticsData(row)?.mandateConflictPlaycountPercentage)
          }}
        </template>

        <template v-slot:mandateConflictPlaycountPercentageDelta="{ row }">
          {{
            getPercentageDeltaFormatted(getTransactionCalculationStatisticsData(row)?.mandateConflictPlaycountPercentageDelta)
          }}
        </template>

        <template v-slot:matchedPlaycountDelta="{ row }">
          {{
            getTransactionCalculationStatisticsData(row)?.matchedPlaycountDelta == null ? null : Intl.NumberFormat(undefined, { signDisplay: "exceptZero" }).format(getTransactionCalculationStatisticsData(row)?.matchedPlaycountDelta)
          }}
        </template>

        <template v-slot:matchedPlaycountPercentage="{ row }">
          {{
            getPercentageAmountFormatted(getTransactionCalculationStatisticsData(row)?.matchedPlaycountPercentage)
          }}
        </template>

        <template v-slot:matchedPlaycountPercentageDelta="{ row }">
          {{
            getPercentageDeltaFormatted(getTransactionCalculationStatisticsData(row)?.matchedPlaycountPercentageDelta)
          }}
        </template>

        <template v-slot:totalPlaycount="{ row}">
          {{ getTransactionCalculationStatisticsData(row)?.totalPlaycount }}
        </template>

        <template v-slot:recordingCount="{ row}">
          {{ getTransactionCalculationStatisticsData(row)?.recordingCount }}
        </template>

        <template v-slot:matchedPlaycount="{ row}">
          {{ getTransactionCalculationStatisticsData(row)?.matchedPlaycount }}
        </template>
        <template v-slot:payableRecordingsCount="{ row }">
          {{
            getTransactionCalculationStatisticsData(row)?.payableRecordingsCount
          }}
        </template>

        <template v-slot:payableRecordingsPercentage="{ row }">
          {{
            getPercentageAmountFormatted(getTransactionCalculationStatisticsData(row)?.payableRecordingsPercentage)
          }}
        </template>

        <template v-slot:payableRecordingsPercentageDelta="{ row }">
          {{
            getPercentageDeltaFormatted(getTransactionCalculationStatisticsData(row)?.payableRecordingsPercentageDelta)
          }}
        </template>

        <template v-slot:nonEligibleRecordingsCount="{ row }">
          {{
            getTransactionCalculationStatisticsData(row)?.nonEligibleRecordingsCount
          }}
        </template>

        <template v-slot:nonEligibleRecordingsPercentage="{ row }">
          {{
            getPercentageAmountFormatted(getTransactionCalculationStatisticsData(row)?.nonEligibleRecordingsPercentage)
          }}
        </template>

        <template v-slot:nonEligibleRecordingsPercentageDelta="{ row }">
          {{
            getPercentageDeltaFormatted(getTransactionCalculationStatisticsData(row)?.nonEligibleRecordingsPercentageDelta)
          }}
        </template>

        <template v-slot:ownershipConflictRecordingsCount="{ row }">
          {{
            getTransactionCalculationStatisticsData(row)?.ownershipConflictRecordingsCount
          }}
        </template>

        <template v-slot:ownershipConflictRecordingsPercentage="{ row }">
          {{
            getPercentageAmountFormatted(getTransactionCalculationStatisticsData(row)?.ownershipConflictRecordingsPercentage)
          }}
        </template>

        <template v-slot:ownershipConflictRecordingsPercentageDelta="{ row }">
          {{
            getPercentageDeltaFormatted(getTransactionCalculationStatisticsData(row)?.ownershipConflictRecordingsPercentageDelta)
          }}
        </template>

        <template v-slot:lineupConflictRecordingsCount="{ row }">
          {{
            getTransactionCalculationStatisticsData(row)?.lineupConflictRecordingsCount
          }}
        </template>

        <template v-slot:lineupConflictRecordingsPercentage="{ row }">
          {{
            getPercentageAmountFormatted(getTransactionCalculationStatisticsData(row)?.lineupConflictRecordingsPercentage)
          }}
        </template>

        <template v-slot:lineupConflictRecordingsPercentageDelta="{ row }">
          {{
            getPercentageDeltaFormatted(getTransactionCalculationStatisticsData(row)?.lineupConflictRecordingsPercentageDelta)
          }}
        </template>

        <template v-slot:mandateConflictRecordingsCount="{ row }">
          {{
            getTransactionCalculationStatisticsData(row)?.mandateConflictRecordingsCount
          }}
        </template>

        <template v-slot:mandateConflictRecordingsPercentage="{ row }">
          {{
            getPercentageAmountFormatted(getTransactionCalculationStatisticsData(row)?.mandateConflictRecordingsPercentage)
          }}
        </template>

        <template v-slot:mandateConflictRecordingsPercentageDelta="{ row }">
          {{
            getPercentageDeltaFormatted(getTransactionCalculationStatisticsData(row)?.mandateConflictRecordingsPercentageDelta)
          }}
        </template>

        <template v-slot:paidAmountMaker="{ row }">
          {{
            getCurrencyAmountFormatted(row.revenueCurrency, row.paidAmountMaker)
          }}
        </template>

        <template v-slot:paidAmountPerformer="{ row }">
          {{
            getCurrencyAmountFormatted(row.revenueCurrency, row.paidAmountPerformer)
          }}
        </template>

        <template v-slot:payableAmountMaker="{ row }">
          {{
            getCurrencyAmountFormatted(row.revenueCurrency, row.payableAmountMaker)
          }}
        </template>

        <template v-slot:payableAmountPerformer="{ row }">
          {{
            getCurrencyAmountFormatted(row.revenueCurrency, row.payableAmountPerformer)
          }}
        </template>

        <template v-slot:availableBalanceMaker="{ row }">
          {{
            getCurrencyAmountFormatted(row.revenueCurrency, row.availableBalanceMaker)
          }}
        </template>

        <template v-slot:availableBalancePerformer="{ row }">
          {{
            getCurrencyAmountFormatted(row.revenueCurrency, row.availableBalancePerformer)
          }}
        </template>

        <template v-slot:moratoriumStatus="{ row }">
          <div v-if="row.moratoriumPerformerActive || row.moratoriumMakerActive">
            <status
                :text="getMoratoriumStatus(row)"
                :icon="['fas', 'exclamation-circle']"
                color="orange"
            />
          </div>
        </template>

        <template v-slot:moratoriumActionApply="{ row }">
          <div v-if="allowMoratoriumApply(row)">
            <b-button class="btn-outline-red" @click="showMoratoriumApplyModal(row.id)">
              {{ $t("moratorium") | capitalize }}
            </b-button>
          </div>
          <modal
              :id="'moratorium-apply-modal-' + row.id"
              :customTitle="$t('place_moratorium') | capitalize"
              modal-class="custom-modal edit"
              modalType="edit"
              size="lg"
              centered
              @ok="setMoratorium(row, true)"
              okTitle="Continue"
              @close="moratoriumModalClose"
          >
            {{ $t("moratorium_apply_text", { distribution: `${row.id} ${row.description}` }) }}
            <span class="d-flex align-items-center" style="margin-top: 15px" >
              College <b-form-select class="ml-2" required v-model="moratoriumCollege" :options="getMoratoriumApplyColleges(row)" style="max-width: 400px;"/>
            </span>
          </modal>
        </template>

        <template v-slot:moratoriumActionRemove="{ row }">
          <div v-if="allowMoratoriumRemove(row)">
            <b-button class="btn-outline-red" @click="showMoratoriumRemoveModal(row.id)">
              {{ $t("remove_moratorium") | capitalize }}
            </b-button>
          </div>
          <modal
              :id="'moratorium-remove-modal-' + row.id"
              :customTitle="$t('removing_moratorium') | capitalize"
              modal-class="custom-modal edit"
              modalType="edit"
              size="lg"
              centered
              @ok="setMoratorium(row, false)"
              okTitle="Continue"
              @close="moratoriumModalClose"
          >
            {{ $t("moratorium_remove_text", { distribution: `${row.id} ${row.description}` }) }}
            <span class="d-flex align-items-center" style="margin-top: 15px" >
              College <b-form-select class="ml-2" required v-model="moratoriumCollege" :options="getMoratoriumRemoveColleges(row)" style="max-width: 400px;"/>
            </span>
          </modal>
        </template>

      </t-table>

    </template>
  </list-layout>

</template>

<script>
import { capitalize, get, snakeCase } from "lodash"
import {
  connectDistributionMethodologies,
  distributionCollegeTypes,
  distributionMethodologies,
  distributionsStatisticsColumnsOptions,
  distributionsStatisticsValuesOptions,
  distributionsTableDisplayOptions,
  eligibilityOptions,
  filterTypes,
  linkTypes,
  perPageItemsLg,
  userPreferenceNames
} from "@/constants"
import { mapActions, mapMutations, mapState } from "vuex"
import DatabaseLink from "@/components/DatabaseLink.vue"
import DistributionsExportForm from "./DistributionsExportForm.vue"
import FilterBuilder from "@/components/FilterBuilder"
import { Pages } from "@/utils/pages"
import Pagination from "@/components/Pagination"
import { listRouteMixin } from "@/utils/mixins"
import parseDate from "@/utils/date-parser"

export default {
  name: "ListDistributionsPage",
  mixins: [listRouteMixin],
  components: {
    DatabaseLink,
    DistributionsExportForm,
    FilterBuilder,
    Pagination,
  },
  computed: {
    ...mapState("consts", ["tariffs"]),
    ...mapState("user", ["user", "permissions"]),
    tariffOptions () {
      return this.tariffs.map(c => {
        return { text: `${c.code} - ${c.text}`, value: c.code }
      })
    },
    columnMode () {
      if (this.displayTable === this.distributionsTableDisplayOptions.DEFAULT)
        return this.displayTable
      else {
        return this.statisticsValues + this.statisticsColumns
      }
    }
  },
  methods: {
    ...mapActions("consts", ["getTariffs"]),
    ...mapMutations("alert", ["error", "success"]),
    parseDate,
    getSelectedTable () {
      return {
        displayTable: this.displayTable,
        statisticsValues: this.statisticsValues,
        statisticsColumns: this.statisticsColumns
      }
    },
    getCurrencyAmountFormatted (currency, value) {
      if (!this.formatters["currency_amount"][currency]) {
        this.formatters["currency_amount"][currency] = Intl.NumberFormat(undefined, {
          style: "currency",
          currency: currency,
          maximumFractionDigits: 12
        })
      }
      return value == null ? null : this.formatters["currency_amount"][currency].format(value)
    },
    getCurrencyDeltaFormatted (currency, value) {
      if (!this.formatters["currency_delta"][currency]) {
        this.formatters["currency_delta"][currency] = Intl.NumberFormat(undefined, {
          style: "currency",
          currency: currency,
          signDisplay: "exceptZero",
          maximumFractionDigits: 12
        })
      }
      return value == null ? null : this.formatters["currency_delta"][currency].format(value)
    },
    getPercentageAmountFormatted (value) {
      return value == null ? null : this.formatters["percentage_amount"].format(parseFloat(value) / 100)
    },
    getPercentageDeltaFormatted (value) {
      return value == null ? null : this.formatters["percentage_delta"].format(parseFloat(value) / 100)
    },
    getMoratoriumStatus (row) {
      const colleges = []
      if (row.moratoriumMakerActive) {
        colleges.push(capitalize(distributionCollegeTypes.MAKER))
      }
      if (row.moratoriumPerformerActive) {
        colleges.push(capitalize(distributionCollegeTypes.PERFORMER))
      }
      if (colleges.length) {
        return `${colleges.join(" & ")} Moratorium`
      }
      return null
    },
    allowMoratoriumApply (row) {
      return (!row.moratoriumMakerActive && row.isMoratoriumAlertMaker) || (!row.moratoriumPerformerActive && row.isMoratoriumAlertPerformer)
    },
    allowMoratoriumRemove (row) {
      return row.moratoriumMakerActive || row.moratoriumPerformerActive
    },
    onDelete (id) {
      this.$bvModal.show(`confirmation-modal-${id}`)
    },
    showMoratoriumRemoveModal (id) {
      this.$bvModal.show(`moratorium-remove-modal-${id}`)
    },
    showMoratoriumApplyModal (id) {
      this.$bvModal.show(`moratorium-apply-modal-${id}`)
    },
    getMoratoriumRemoveColleges (row) {
      const options = []
      if (row.moratoriumMakerActive) {
        options.push(
          {
            text: capitalize(this.$t("maker")),
            value: distributionCollegeTypes.MAKER,
          }
        )
      }
      if (row.moratoriumPerformerActive) {
        options.push(
          {
            text: capitalize(this.$t("performer")),
            value: distributionCollegeTypes.PERFORMER,
          }
        )
      }
      if (row.moratoriumMakerActive && row.moratoriumPerformerActive) {
        options.push(
          {
            text: capitalize(this.$t("maker_and_performer")),
            value: distributionCollegeTypes.MAKER_AND_PERFORMER,
          }
        )
      }
      return options
    },
    getMoratoriumApplyColleges (row) {
      const options = []
      if (!row.moratoriumMakerActive && row.isMoratoriumAlertMaker) {
        options.push(
          {
            text: capitalize(this.$t("maker")),
            value: distributionCollegeTypes.MAKER,
          }
        )
      }
      if (!row.moratoriumPerformerActive && row.isMoratoriumAlertPerformer) {
        options.push(
          {
            text: capitalize(this.$t("performer")),
            value: distributionCollegeTypes.PERFORMER,
          }
        )
      }
      if (!row.moratoriumMakerActive && row.isMoratoriumAlertMaker && !row.moratoriumPerformerActive && row.isMoratoriumAlertPerformer) {
        options.push(
          {
            text: capitalize(this.$t("maker_and_performer")),
            value: distributionCollegeTypes.MAKER_AND_PERFORMER,
          }
        )
      }
      return options
    },
    moratoriumModalClose () {
      this.moratoriumCollege = null
    },
    onConfirmDelete (id) {
      this.$api.distributions
        .deleteDistribution(id)
        .then(() => {
          this.getData()
        })
        .catch(error => {
          let msg = `Error deleting distribution. ${error.response.data.nonFieldErrors[0].message}`
          this.error(msg)
        })
    },
    getMoratoriumPatchObject (moratorium) {
      const patchObj = {}
      if (this.moratoriumCollege === distributionCollegeTypes.MAKER_AND_PERFORMER) {
        patchObj["moratorium.maker.active"] = moratorium
        patchObj["moratorium.performer.active"] = moratorium
      } else if (this.moratoriumCollege === distributionCollegeTypes.MAKER) {
        patchObj["moratorium.maker.active"] = moratorium
      } else if (this.moratoriumCollege === distributionCollegeTypes.PERFORMER) {
        patchObj["moratorium.performer.active"] = moratorium
      }
      return patchObj
    },
    getMoratoriumCollegesString () {
      const colleges = []
      if (this.moratoriumCollege === distributionCollegeTypes.MAKER_AND_PERFORMER) {
        colleges.push("Maker", "Performer")
      } else if (this.moratoriumCollege === distributionCollegeTypes.MAKER) {
        colleges.push("Maker")
      } else if (this.moratoriumCollege === distributionCollegeTypes.PERFORMER) {
        colleges.push("Performer")
      }
      return colleges.join(", ")
    },
    setMoratorium (distribution, moratorium) {
      const patchObject = this.getMoratoriumPatchObject(moratorium)
      const collegesString = this.getMoratoriumCollegesString()
      this.$api.distributions
        .updateDistribution(distribution.id, patchObject)
        .then(() => {
          this.cachedData = null
          this.$nextTick(() => this.getData())
          this.success(this.$t(moratorium ? "moratorium_applied_success" : "moratorium_removed_success",
            { colleges: collegesString, distribution: `${distribution.id} ${distribution.description}` }))
        })
        .catch(error => {
          let msg = `Error updating distribution moratorium. ${error.response.data.nonFieldErrors[0].message}`
          this.error(msg)
        })
    },
    onFilter (name, value) {
      this.cachedData = null
      if (["periodStart", "periodEnd"].includes(name)) {
        const range = (value) ?
          [
            this.parseDate(value[0], this.$config.ISO_DATE_FORMAT),
            this.parseDate(value[1], this.$config.ISO_DATE_FORMAT)
          ] : []

        if (range.filter(r => r).length) {
          this.filters.userTimezone.value = Intl.DateTimeFormat().resolvedOptions().timeZone
        } else {
          if (name === "periodStart") {
            this.filters.periodStart.value = this.filters.periodStart.defaultValue
          } else {
            this.filters.periodEnd.value = this.filters.periodEnd.defaultValue
          }
          this.filters.userTimezone.value = this.filters.userTimezone.defaultValue
        }
        this.changeFilter(name, range)
      }
    },
    exportData () {
      this.$bvModal.show("distributions-export-modal")
    },
    onLoaded ({ data }) {
      this.count = data.count
      this.loading = false
      this.cachedData = { data: data }
      this.latestCalculation = data.latestCalculation
      this.nextCalculation = data.nextCalculation
    },
    getFiltersWithOptions () {
      let collegeOptions = [...this.rightsholderOptions]
      collegeOptions.push({
        text: capitalize(this.$t(distributionCollegeTypes.MAKER_AND_PERFORMER)),
        value: distributionCollegeTypes.MAKER_AND_PERFORMER
      })
      // Let's sort alphabetically after the insert
      collegeOptions.sort((a, b) => a.value.localeCompare(b.value))
      this.filters.college.options = collegeOptions
      this.filters.tariff.options = this.tariffOptions
      this.filters.proxyTariff.options = this.tariffOptions
      return this.filters
    },
    getData () {
      this.$refs.distributions.getData()
    },
    onPaginationClearCached (page) {
      this.cachedData = null
      this.$nextTick(() => this.onPagination(page))
    },
    onLimitClearCached (limit, reference) {
      this.cachedData = null
      this.$nextTick(() => this.onLimit(limit, reference))
    },
    onSorted (data) {
      this.cachedData = null
      this.queryParams = {
        ...this.queryParams,
        ["sorting"]: data.map(item => item.ascending ? this.getColumnSorting(item.column) : `-${this.getColumnSorting(item.column)}`)
      }
      this.$nextTick(() => this.applyFilters())
    },
    getColumnSorting (column) {
      let columnSnakeCase = snakeCase(column)
      for (let [prefix, fields] of Object.entries(this.prefixToFieldsMap)) {
        if (fields.includes(column)) {
          columnSnakeCase = prefix + columnSnakeCase
          break
        }
      }
      const sortColumnMap = {
        "vpp_performer": "vpp__performer",
        "vpp_maker": "vpp__maker",
        "vpp_delta_performer": "vpp_delta__performer",
        "vpp_delta_maker": "vpp_delta__maker",
      }
      if (typeof sortColumnMap[columnSnakeCase] !== "undefined") {
        columnSnakeCase = sortColumnMap[columnSnakeCase]
      }

      return columnSnakeCase
    },
    getLastSuccessfulCalculationData (row) {
      return row.transactionInfo?.calculations?.findLast(c => c.status === "success")
    },
    getTransactionCalculationData (row) {
      return this.getLastSuccessfulCalculationData(row)
    },
    getTransactionCalculationStatisticsData (row) {
      return this.getTransactionCalculationData(row)?.statistics
    },
    tariffDisplay (code) {
      let tariff = this.tariffs.find(t => t.code === code)
      if (tariff) {
        return `${tariff.code} - ${tariff.text}`
      }
      return ""
    }
  },
  watch: {
    "filters.distributionMethodology.value": function (value) {
      this.cachedData = null
      if (value === distributionMethodologies.CONNECT) {
        value = connectDistributionMethodologies
      }
      this.changeFilter("distributionMethodology", value, true)
    },
  },
  data () {
    const columns = [
      "id", "description", "tariff", "periodStart", "periodEnd", "college", "proxyTariff", "primaryEligibilityScheme",
      "secondaryEligibilityScheme", "paidAmountMaker", "paidAmountPerformer", "payableAmountMaker",
      "payableAmountPerformer", "availableBalanceMaker", "availableBalancePerformer", "moratoriumActionApply", "moratoriumActionRemove", "moratoriumStatus",
      "calculationTime", "vppPerformer", "vppDeltaPerformer", "vppMaker", "vppDeltaMaker", "revenueTotal", "payableRecordingsCount",
      "payableRecordingsPercentage", "payableRecordingsPercentageDelta", "totalPlaycount", "recordingCount",
      "matchedPlaycount", "nonEligibleRecordingsCount", "nonEligibleRecordingsPercentage",
      "nonEligibleRecordingsPercentageDelta", "ownershipConflictRecordingsCount",
      "ownershipConflictRecordingsPercentage", "ownershipConflictRecordingsPercentageDelta",
      "lineupConflictRecordingsCount", "lineupConflictRecordingsPercentage", "lineupConflictRecordingsPercentageDelta",
      "mandateConflictRecordingsCount", "mandateConflictRecordingsPercentage", "mandateConflictRecordingsPercentageDelta",
      "matchedPlaycountDelta", "matchedPlaycountPercentage", "matchedPlaycountPercentageDelta", "payablePlaycount",
      "payablePlaycountPercentage", "payablePlaycountPercentageDelta", "nonEligiblePlaycount",
      "nonEligiblePlaycountPercentage", "nonEligiblePlaycountPercentageDelta", "ownershipConflictPlaycount",
      "ownershipConflictPlaycountPercentage", "ownershipConflictPlaycountPercentageDelta", "lineupConflictPlaycount",
      "lineupConflictPlaycountPercentage", "lineupConflictPlaycountPercentageDelta", "mandateConflictPlaycount",
      "mandateConflictPlaycountPercentage", "mandateConflictPlaycountPercentageDelta", "actions",
    ]
    const enabledColumns = {
      default: [
        "id",
        "description",
        "tariff",
        "periodStart",
        "periodEnd",
        "college",
        "proxyTariff",
        "primaryEligibilityScheme",
        "secondaryEligibilityScheme",
        "paidAmountMaker",
        "paidAmountPerformer",
        "payableAmountMaker",
        "payableAmountPerformer",
        "availableBalanceMaker",
        "availableBalancePerformer",
        "moratoriumActionApply",
        "moratoriumActionRemove",
        "moratoriumStatus",
        "actions"
      ],
      playcountall: [
        "id",
        "description",
        "calculationTime",
        "revenueTotal",
        "vppPerformer",
        "vppMaker",
        "vppDeltaPerformer",
        "vppDeltaMaker",
        "totalPlaycount",
        "matchedPlaycount",
        "matchedPlaycountDelta",
        "matchedPlaycountPercentage",
        "matchedPlaycountPercentageDelta",
        "payablePlaycount",
        "payablePlaycountPercentage",
        "payablePlaycountPercentageDelta",
        "nonEligiblePlaycount",
        "nonEligiblePlaycountPercentage",
        "nonEligiblePlaycountPercentageDelta",
        "ownershipConflictPlaycount",
        "ownershipConflictPlaycountPercentage",
        "ownershipConflictPlaycountPercentageDelta",
        "lineupConflictPlaycount",
        "lineupConflictPlaycountPercentage",
        "lineupConflictPlaycountPercentageDelta",
        "mandateConflictPlaycount",
        "mandateConflictPlaycountPercentage",
        "mandateConflictPlaycountPercentageDelta",
        "actions"
      ],
      playcountcount: [
        "id",
        "description",
        "calculationTime",
        "revenueTotal",
        "vppPerformer",
        "vppMaker",
        "vppDeltaPerformer",
        "vppDeltaMaker",
        "totalPlaycount",
        "matchedPlaycount",
        "payablePlaycount",
        "nonEligiblePlaycount",
        "ownershipConflictPlaycount",
        "lineupConflictPlaycount",
        "mandateConflictPlaycount",
        "actions"
      ],
      playcountpercentage: [
        "id",
        "description",
        "calculationTime",
        "revenueTotal",
        "vppPerformer",
        "vppMaker",
        "vppDeltaPerformer",
        "vppDeltaMaker",
        "matchedPlaycountPercentage",
        "payablePlaycountPercentage",
        "nonEligiblePlaycountPercentage",
        "ownershipConflictPlaycountPercentage",
        "lineupConflictPlaycountPercentage",
        "mandateConflictPlaycountPercentage",
        "actions"
      ],
      playcountdelta: [
        "id",
        "description",
        "calculationTime",
        "revenueTotal",
        "vppPerformer",
        "vppMaker",
        "vppDeltaPerformer",
        "vppDeltaMaker",
        "matchedPlaycountDelta",
        "matchedPlaycountPercentageDelta",
        "payablePlaycountPercentageDelta",
        "nonEligiblePlaycountPercentageDelta",
        "ownershipConflictPlaycountPercentageDelta",
        "lineupConflictPlaycountPercentageDelta",
        "mandateConflictPlaycountPercentageDelta",
        "actions"
      ],
      recordingsall: [
        "id",
        "description",
        "calculationTime",
        "revenueTotal",
        "vppPerformer",
        "vppMaker",
        "vppDeltaPerformer",
        "vppDeltaMaker",
        "recordingCount",
        "payableRecordingsCount",
        "payableRecordingsPercentage",
        "payableRecordingsPercentageDelta",
        "nonEligibleRecordingsCount",
        "nonEligibleRecordingsPercentage",
        "nonEligibleRecordingsPercentageDelta",
        "ownershipConflictRecordingsCount",
        "ownershipConflictRecordingsPercentage",
        "ownershipConflictRecordingsPercentageDelta",
        "lineupConflictRecordingsCount",
        "lineupConflictRecordingsPercentage",
        "lineupConflictRecordingsPercentageDelta",
        "mandateConflictRecordingsCount",
        "mandateConflictRecordingsPercentage",
        "mandateConflictRecordingsPercentageDelta",
        "actions"
      ],
      recordingscount: [
        "id",
        "description",
        "calculationTime",
        "revenueTotal",
        "vppPerformer",
        "vppMaker",
        "vppDeltaPerformer",
        "vppDeltaMaker",
        "recordingCount",
        "payableRecordingsCount",
        "nonEligibleRecordingsCount",
        "ownershipConflictRecordingsCount",
        "lineupConflictRecordingsCount",
        "mandateConflictRecordingsCount",
        "actions"
      ],
      recordingspercentage: [
        "id",
        "description",
        "calculationTime",
        "revenueTotal",
        "vppPerformer",
        "vppMaker",
        "vppDeltaPerformer",
        "vppDeltaMaker",
        "payableRecordingsPercentage",
        "nonEligibleRecordingsPercentage",
        "ownershipConflictRecordingsPercentage",
        "lineupConflictRecordingsPercentage",
        "mandateConflictRecordingsPercentage",
        "actions"
      ],
      recordingsdelta: [
        "id",
        "description",
        "calculationTime",
        "revenueTotal",
        "vppPerformer",
        "vppMaker",
        "vppDeltaPerformer",
        "vppDeltaMaker",
        "payableRecordingsPercentageDelta",
        "nonEligibleRecordingsPercentageDelta",
        "ownershipConflictRecordingsPercentageDelta",
        "lineupConflictRecordingsPercentageDelta",
        "mandateConflictRecordingsPercentageDelta",
        "actions"
      ]
    }
    const columnConfigurations = {
      ...enabledColumns,
      playcountall: [
        "id",
        "description",
        "calculationTime",
        "revenueTotal",
        "vppPerformer",
        "vppMaker",
        "vppDeltaPerformer",
        "vppDeltaMaker",
        "totalPlaycount",
        "matchedPlaycountPercentage",
        "payablePlaycountPercentage",
        "ownershipConflictPlaycountPercentage",
        "lineupConflictPlaycountPercentage",
        "mandateConflictPlaycountPercentage",
        "actions"
      ],
      recordingsall: [
        "id",
        "description",
        "calculationTime",
        "revenueTotal",
        "vppPerformer",
        "vppMaker",
        "vppDeltaPerformer",
        "vppDeltaMaker",
        "payableRecordingsCount",
        "payableRecordingsPercentage",
        "nonEligibleRecordingsPercentage",
        "ownershipConflictRecordingsPercentage",
        "lineupConflictRecordingsPercentage",
        "mandateConflictRecordingsPercentage",
        "actions"
      ]
    }
    const options = {
      allSelected: false,
      sortable: [
        "id",
        "description",
        "tariff",
        "periodStart",
        "periodEnd",
        "college",
        "proxyTariff",
        "calculationTime",
        "revenueTotal",
        "vppPerformer",
        "vppMaker",
        "vppDeltaPerformer",
        "vppDeltaMaker",
        "totalPlaycount",
        "recordingCount",
        "matchedPlaycount",
        "payableRecordingsCount",
        "payableRecordingsPercentage",
        "payableRecordingsPercentageDelta",
        "nonEligibleRecordingsCount",
        "nonEligibleRecordingsPercentage",
        "nonEligibleRecordingsPercentageDelta",
        "ownershipConflictRecordingsCount",
        "ownershipConflictRecordingsPercentage",
        "ownershipConflictRecordingsPercentageDelta",
        "lineupConflictRecordingsCount",
        "lineupConflictRecordingsPercentage",
        "lineupConflictRecordingsPercentageDelta",
        "mandateConflictRecordingsCount",
        "mandateConflictRecordingsPercentage",
        "mandateConflictRecordingsPercentageDelta",
        "matchedPlaycountDelta",
        "matchedPlaycountPercentage",
        "matchedPlaycountPercentageDelta",
        "payablePlaycount",
        "payablePlaycountPercentage",
        "payablePlaycountPercentageDelta",
        "nonEligiblePlaycount",
        "nonEligiblePlaycountPercentage",
        "nonEligiblePlaycountPercentageDelta",
        "ownershipConflictPlaycount",
        "ownershipConflictPlaycountPercentage",
        "ownershipConflictPlaycountPercentageDelta",
        "lineupConflictPlaycount",
        "lineupConflictPlaycountPercentage",
        "lineupConflictPlaycountPercentageDelta",
        "mandateConflictPlaycount",
        "mandateConflictPlaycountPercentage",
        "mandateConflictPlaycountPercentageDelta",
        "paidAmountMaker",
        "paidAmountPerformer",
        "payableAmountMaker",
        "payableAmountPerformer",
        "availableBalanceMaker",
        "availableBalancePerformer"
      ],
      headings: {
        "id": capitalize(this.$t("id")),
        "description": capitalize(this.$t("description")),
        "tariff": capitalize(this.$t("tariff")),
        "periodStart": capitalize(this.$t("start_date")),
        "periodEnd": capitalize(this.$t("end_date")),
        "college": capitalize(this.$t("college")),
        "proxyTariff": capitalize(this.$t("proxy_tariff")),
        "primaryEligibilityScheme": capitalize(this.$t("eligibility_scheme")),
        "secondaryEligibilityScheme": capitalize(this.$t("backup_eligibility_scheme")),
        "actions": "",
        "calculationTime": capitalize(this.$t("calculation_time")),
        "vppPerformer": this.$t("vpp_performer"),
        "vppMaker": this.$t("vpp_maker"),
        "vppDeltaPerformer": this.$t("vpp_performer_delta"),
        "vppDeltaMaker": this.$t("vpp_maker_delta"),
        "revenueTotal": capitalize(this.$t("revenue_total")),
        "payableRecordingsCount": capitalize(this.$t("payable_recordings")),
        "payableRecordingsPercentage": capitalize(this.$t("payable_recordings_percent")),
        "payableRecordingsPercentageDelta": capitalize(this.$t("payable_recordings_delta_percent")),
        "totalPlaycount": capitalize(this.$t("total_playcount")),
        "recordingCount": capitalize(this.$t("total_recordings")),
        "matchedPlaycount": capitalize(this.$t("matched_playcount")),
        "nonEligibleRecordingsCount": this.$t("non_eligible_recordings"),
        "nonEligibleRecordingsPercentage": this.$t("non_eligible_recordings_percentage"),
        "nonEligibleRecordingsPercentageDelta": this.$t("non_eligible_recordings_percentage_delta"),
        "ownershipConflictRecordingsCount": this.$t("ownership_conflict_recordings"),
        "ownershipConflictRecordingsPercentage": this.$t("ownership_conflict_recordings_percentage"),
        "ownershipConflictRecordingsPercentageDelta": this.$t("ownership_conflict_recordings_percentage_delta"),
        "lineupConflictRecordingsCount": this.$t("lineup_conflict_recordings"),
        "lineupConflictRecordingsPercentage": this.$t("lineup_conflict_recordings_percentage"),
        "lineupConflictRecordingsPercentageDelta": this.$t("lineup_conflict_recordings_percentage_delta"),
        "mandateConflictRecordingsCount": this.$t("mandate_conflict_recordings"),
        "mandateConflictRecordingsPercentage": this.$t("mandate_conflict_recordings_percentage"),
        "mandateConflictRecordingsPercentageDelta": this.$t("mandate_conflict_recordings_percentage_delta"),
        "matchedPlaycountDelta": this.$t("matched_playcount_delta"),
        "matchedPlaycountPercentage": this.$t("matched_playcount_percentage"),
        "matchedPlaycountPercentageDelta": this.$t("matched_playcount_percentage_delta"),
        "payablePlaycount": this.$t("payable_playcount"),
        "payablePlaycountPercentage": this.$t("payable_playcount_percentage"),
        "payablePlaycountPercentageDelta": this.$t("payable_playcount_percentage_delta"),
        "nonEligiblePlaycount": this.$t("non_eligible_playcount"),
        "nonEligiblePlaycountPercentage": this.$t("non_eligible_playcount_percentage"),
        "nonEligiblePlaycountPercentageDelta": this.$t("non_eligible_playcount_percentage_delta"),
        "ownershipConflictPlaycount": this.$t("ownership_conflict_playcount"),
        "ownershipConflictPlaycountPercentage": this.$t("ownership_conflict_playcount_percentage"),
        "ownershipConflictPlaycountPercentageDelta": this.$t("ownership_conflict_playcount_percentage_delta"),
        "lineupConflictPlaycount": this.$t("lineup_conflict_playcount"),
        "lineupConflictPlaycountPercentage": this.$t("lineup_conflict_playcount_percentage"),
        "lineupConflictPlaycountPercentageDelta": this.$t("lineup_conflict_playcount_percentage_delta"),
        "mandateConflictPlaycount": this.$t("mandate_conflict_playcount"),
        "mandateConflictPlaycountPercentage": this.$t("mandate_conflict_playcount_percentage"),
        "mandateConflictPlaycountPercentageDelta": this.$t("mandate_conflict_playcount_percentage_delta"),
        "paidAmountMaker": capitalize(this.$t("paid_amount_maker")),
        "paidAmountPerformer": capitalize(this.$t("paid_amount_performer")),
        "payableAmountMaker": capitalize(this.$t("payable_amount_maker")),
        "payableAmountPerformer": capitalize(this.$t("payable_amount_performer")),
        "availableBalanceMaker": capitalize(this.$t("available_balance_maker")),
        "availableBalancePerformer": capitalize(this.$t("available_balance_performer")),
        "moratoriumStatus": capitalize(this.$t("status")),
      },
      width: {
        "actions": "50",
      },
      columnsClasses: {},
      enabledColumns: enabledColumns,
      columnConfigurations: columnConfigurations,
      rowClasses: (row) => {
        if ((row.isMoratoriumAlertMaker && !row.moratoriumMakerActive) || (row.isMoratoriumAlertPerformer && !row.moratoriumPerformerActive)) {
          return ["moratorium-active"]
        }
        return []
      },
      responseAdapter ({ data }) {
        return {
          data: data.results,
          count: data.count,
        }
      },
      requestFunction (queryParams) {
        queryParams = { ...queryParams, ...this.$route.query }
        queryParams.distribution_methodology = get(this.$route.query, "distribution_methodology", distributionMethodologies.STANDARD)
        if (queryParams.distribution_methodology === distributionMethodologies.CONNECT) {
          queryParams.distribution_methodology = connectDistributionMethodologies
        }
        if (queryParams.college) {
          if (queryParams.college === distributionCollegeTypes.MAKER || queryParams.college === distributionCollegeTypes.PERFORMER) {
            queryParams.college = [queryParams.college, distributionCollegeTypes.MAKER_AND_PERFORMER]
          }
        }
        return this.$api.distributions.distributionsList(queryParams)
      },
    }
    return {
      moratoriumCollege: null,
      cachedData: null,
      loading: false,
      page: 1,
      count: 0,
      limit: perPageItemsLg,
      pageNumber: 1,
      userPreferenceNames,
      distributionMethodologies,
      linkTypes,
      distributionsTableDisplayOptions,
      distributionsStatisticsValuesOptions,
      distributionsStatisticsColumnsOptions,
      queryParams: {},
      displayTable: distributionsTableDisplayOptions.DEFAULT,
      statisticsValues: distributionsStatisticsValuesOptions.PLAYCOUNT,
      statisticsColumns: distributionsStatisticsColumnsOptions.ALL,
      columns: columns,
      formatters: {
        "currency_amount": {},
        "currency_delta": {},
        "percentage_amount": Intl.NumberFormat(undefined, { style: "percent" }),
        "percentage_delta": Intl.NumberFormat(undefined, { style: "percent", signDisplay: "exceptZero" })
      },
      options: options,
      prefixToFieldsMap: {
        latest_transaction_calculations__statistics__: [
          "totalPlaycount",
          "recordingCount",
          "matchedPlaycount",
          "payableRecordingsCount",
          "payableRecordingsPercentage",
          "payableRecordingsPercentageDelta",
          "nonEligibleRecordingsCount",
          "nonEligibleRecordingsPercentage",
          "nonEligibleRecordingsPercentageDelta",
          "ownershipConflictRecordingsCount",
          "ownershipConflictRecordingsPercentage",
          "ownershipConflictRecordingsPercentageDelta",
          "lineupConflictRecordingsCount",
          "lineupConflictRecordingsPercentage",
          "lineupConflictRecordingsPercentageDelta",
          "mandateConflictRecordingsCount",
          "mandateConflictRecordingsPercentage",
          "mandateConflictRecordingsPercentageDelta",
          "matchedPlaycountDelta",
          "matchedPlaycountPercentage",
          "matchedPlaycountPercentageDelta",
          "payablePlaycount",
          "payablePlaycountPercentage",
          "payablePlaycountPercentageDelta",
          "nonEligiblePlaycount",
          "nonEligiblePlaycountPercentage",
          "nonEligiblePlaycountPercentageDelta",
          "ownershipConflictPlaycount",
          "ownershipConflictPlaycountPercentage",
          "ownershipConflictPlaycountPercentageDelta",
          "lineupConflictPlaycount",
          "lineupConflictPlaycountPercentage",
          "lineupConflictPlaycountPercentageDelta",
          "mandateConflictPlaycount",
          "mandateConflictPlaycountPercentage",
          "mandateConflictPlaycountPercentageDelta"
        ],
        latest_transaction_calculations__: [
          "calculationTime"
        ],
      },
      latestCalculation: null,
      nextCalculation: null,
      filters: {
        search: { value: "", defaultValue: "", type: filterTypes.TEXT, label: capitalize(this.$t("search")) },
        distributionMethodology: {
          value: get(this.$route.query, "distributionMethodology", distributionMethodologies.STANDARD),
          defaultValue: distributionMethodologies.STANDARD, hidden: true
        },
        userTimezone: { value: null, defaultValue: null, hidden: true },
        periodStart: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE_RANGE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("start_date")),
          noWatch: true,
        },
        periodEnd: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE_RANGE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("end_date")),
          noWatch: true,
        },
        college: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("college")),
          emtpyValue: capitalize(this.$t("maker_and_performer")),
          type: filterTypes.SELECT,
          options: [],
        },
        tariff: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("tariff")),
          options: [],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
        },
        proxyTariff: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("proxy_tariff")),
          options: [],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
        },
        primaryEligibilityScheme: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("primary_eligibility_scheme")),
          options: eligibilityOptions,
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
        },
        secondaryEligibilityScheme: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("backup_eligibility_scheme")),
          options: eligibilityOptions,
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
        },
      },
      Pages
    }
  },
  mounted () {
    if (!this.tariffs.length) {
      this.getTariffs()
    }
  }
}
</script>

<style scoped lang="scss">
.calculation-times {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    color: var(--gray);

    div:first-child {
        margin-right: 20px;
    }
}

:deep(.moratorium-active) {
    background-color: #FCE4BF !important;
}
</style>

